import { ApiError } from '@/api/client'
import {
  fetchRecipeExport,
  useDeleteRecipe,
  useRecipe,
  useRecordPrep,
  useRemovePrep,
  useSetExcluded,
  useSetPreparations,
} from '@/api/hooks'
import { Button } from '@/components/ui/Button'
import { buttonClasses } from '@/components/ui/buttonStyles'
import { ErrorNote, PageLoader } from '@/components/ui/Feedback'
import { Input } from '@/components/ui/Field'
import { NutritionSummary } from '@/components/ui/Macro'
import { MarkdownView } from '@/components/ui/Markdown'
import { Menu } from '@/components/ui/Menu'
import { Note } from '@/components/ui/Note'
import { ConfirmDialog, Dialog } from '@/components/ui/Overlay'
import { RecipeCover } from '@/components/ui/RecipeCover'
import { useToast } from '@/components/ui/toastContext'
import { PageHeader, PageShell } from '@/components/layout/Page'
import { formatMacro, pluralise, relativeFromNow } from '@/lib/format'
import { downloadJson, slugify } from '@/lib/download'
import { useDocumentTitle } from '@/lib/useDocumentTitle'
import {
  Ban,
  CalendarCheck,
  CalendarDays,
  Download,
  ExternalLink,
  Pencil,
  RotateCcw,
  Trash2,
  Undo2,
} from 'lucide-react'
import { useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { IngredientList } from './IngredientList'
import { PreparationEditor } from './PreparationEditor'
import { RecipeFacts } from './RecipeFacts'

export function RecipeDetailPage() {
  const { id } = useParams<{ id: string }>()
  const recipeId = Number(id)
  const navigate = useNavigate()
  const toast = useToast()
  const query = useRecipe(recipeId)
  useDocumentTitle(
    query.data?.recipe.name ?? (query.isError ? 'Recipe not found' : 'Recipe'),
  )
  const setExcluded = useSetExcluded()
  const recordPrep = useRecordPrep()
  const removePrep = useRemovePrep()
  const setPreparations = useSetPreparations()
  const remove = useDeleteRecipe()

  const [excludeOpen, setExcludeOpen] = useState(false)
  const [reason, setReason] = useState('')
  const [deleteOpen, setDeleteOpen] = useState(false)
  const [historyOpen, setHistoryOpen] = useState(false)
  const [draft, setDraft] = useState<string[] | null>(null)
  const [showTotals, setShowTotals] = useState(false)

  if (query.isPending) {
    return (
      <PageShell>
        <PageLoader label="Loading recipe…" />
      </PageShell>
    )
  }

  if (query.isError) {
    return (
      <PageShell>
        <ErrorNote
          title="Recipe not found"
          body={
            query.error instanceof ApiError
              ? query.error.message
              : 'Could not load the recipe.'
          }
          onRetry={() => void query.refetch()}
        />
      </PageShell>
    )
  }

  const detail = query.data
  const recipe = detail.recipe

  async function toggleExclusion(exclude: boolean, why?: string) {
    try {
      await setExcluded.mutateAsync({ id: recipe.id, exclude, reason: why })
      toast.success(exclude ? 'Excluded from recommendations.' : 'Back in recommendations.')
    } catch (error) {
      toast.error(error instanceof ApiError ? error.message : 'Could not update.')
    }
  }

  async function markPrepared() {
    try {
      const result = await recordPrep.mutateAsync([recipe.id])
      toast.success(
        result.changed.length === 0
          ? 'Already recorded as prepared today.'
          : 'Marked as prepared today.',
      )
    } catch (error) {
      toast.error(error instanceof ApiError ? error.message : 'Could not record this.')
    }
  }

  async function undoPrepared() {
    try {
      const result = await removePrep.mutateAsync({ recipeIds: [recipe.id] })
      toast.success(
        result.changed.length === 0
          ? 'No preparations to undo.'
          : 'Removed the most recent preparation.',
      )
    } catch (error) {
      toast.error(error instanceof ApiError ? error.message : 'Could not undo that.')
    }
  }

  async function saveHistory() {
    if (draft === null) return
    try {
      await setPreparations.mutateAsync({ id: recipe.id, dates: draft })
      toast.success('Preparations updated.')
      setHistoryOpen(false)
    } catch (error) {
      toast.error(error instanceof ApiError ? error.message : 'Could not update.')
    }
  }

  async function exportRecipe() {
    try {
      downloadJson(`${slugify(recipe.name)}.json`, await fetchRecipeExport([recipe.id]))
    } catch (error) {
      toast.error(error instanceof ApiError ? error.message : 'Could not export.')
    }
  }

  async function confirmDelete() {
    try {
      await remove.mutateAsync(recipe.id)
      toast.success('Recipe deleted.')
      navigate('/recipes', { replace: true })
    } catch (error) {
      toast.error(error instanceof ApiError ? error.message : 'Could not delete.')
    }
  }

  const totals = detail.nutrition.totals

  return (
    <PageShell>
      <PageHeader
        title={recipe.name}
        actions={
          <>
            <Menu
              items={[
                {
                  label: 'Undo last preparation',
                  icon: <Undo2 size={16} />,
                  disabled: recipe.prep_count === 0,
                  onSelect: () => void undoPrepared(),
                },
                {
                  label: 'Edit preparation history',
                  icon: <CalendarDays size={16} />,
                  onSelect: () => {
                    setDraft(detail.preparations)
                    setHistoryOpen(true)
                  },
                },
                recipe.excluded
                  ? {
                      label: 'Include in recommendations',
                      icon: <RotateCcw size={16} />,
                      onSelect: () => void toggleExclusion(false),
                    }
                  : {
                      label: 'Exclude from recommendations',
                      icon: <Ban size={16} />,
                      onSelect: () => setExcludeOpen(true),
                    },
                {
                  label: 'Export as JSON',
                  icon: <Download size={16} />,
                  onSelect: () => void exportRecipe(),
                },
                {
                  label: 'Delete recipe',
                  icon: <Trash2 size={16} />,
                  destructive: true,
                  onSelect: () => setDeleteOpen(true),
                },
              ]}
            />
            <Link to={`/recipes/${recipe.id}/edit`} className={buttonClasses('secondary', 'sm')}>
              <Pencil size={14} aria-hidden />
              Edit
            </Link>
            <Button
              variant="primary"
              size="sm"
              loading={recordPrep.isPending}
              onClick={() => void markPrepared()}
            >
              <CalendarCheck size={14} aria-hidden />
              Mark prepared
            </Button>
          </>
        }
      />

      <div className="space-y-8">
        <div className="space-y-4">
          {recipe.image_url ? (
            <RecipeCover
              imageUrl={recipe.image_url}
              cuisine={recipe.cuisine}
              className="aspect-[21/9] max-h-80 w-full rounded-[var(--radius-card)]"
            />
          ) : null}
          <RecipeFacts recipe={recipe} tags />
          <p className="text-sm text-ink-faint">
            {recipe.prep_count > 0
              ? `Made ${pluralise(recipe.prep_count, 'time')}, last ${relativeFromNow(recipe.last_prep)}`
              : 'Never made yet'}
          </p>
          {recipe.excluded ? (
            <Note tone="critical">
              Excluded from recommendations
              {recipe.exclusion_reason ? `: ${recipe.exclusion_reason}` : '.'}
            </Note>
          ) : null}
        </div>

        <section className="rounded-[var(--radius-card)] border border-line/70 bg-surface p-5 shadow-card">
          {detail.nutrition.has_data ? (
            <>
              <div className="mb-4 flex flex-wrap items-baseline justify-between gap-2">
                <h2 className="text-base font-semibold text-ink">
                  Nutrition{' '}
                  <span className="font-normal text-ink-faint">· per serving</span>
                </h2>
                <button
                  type="button"
                  onClick={() => setShowTotals((value) => !value)}
                  className="text-xs text-ink-faint hover:text-ink"
                  aria-expanded={showTotals}
                >
                  {showTotals ? 'Hide whole recipe' : 'Show whole recipe'}
                </button>
              </div>
              <NutritionSummary nutrition={detail.nutrition} />
              {showTotals ? (
                <p className="tabular mt-4 border-t border-line pt-3 text-sm text-ink-soft">
                  Whole recipe ({recipe.servings} servings):{' '}
                  {formatMacro(totals.calories)} kcal · {formatMacro(totals.protein)}g protein ·{' '}
                  {formatMacro(totals.carbs)}g carbs · {formatMacro(totals.fat)}g fat ·{' '}
                  {formatMacro(totals.fiber)}g fibre
                </p>
              ) : null}
            </>
          ) : (
            <p className="text-sm text-pretty text-ink-faint">
              No nutrition data yet. Add macros to the ingredients to see it here.
            </p>
          )}
        </section>

        <div className="grid gap-8 lg:grid-cols-[22rem_minmax(0,1fr)] lg:items-start">
          <div className="lg:sticky lg:top-24">
            <IngredientList
              key={recipe.id}
              lines={detail.ingredients}
              servings={recipe.servings}
            />
          </div>

          <div className="min-w-0 space-y-8">
            {recipe.notes ? (
              <section>
                <h2 className="text-base font-semibold text-ink">Notes</h2>
                <p className="mt-2 text-sm whitespace-pre-wrap text-pretty text-ink-soft">
                  {recipe.notes}
                </p>
              </section>
            ) : null}
            <section>
              <h2 className="text-base font-semibold text-ink">Instructions</h2>
              <div className="mt-2">
                {recipe.instructions ? (
                  <MarkdownView>{recipe.instructions}</MarkdownView>
                ) : (
                  <p className="text-sm text-ink-faint">No instructions yet.</p>
                )}
              </div>
            </section>
            {recipe.source_url ? (
              <a
                href={recipe.source_url}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1.5 text-sm text-accent hover:underline"
              >
                Original source
                <ExternalLink size={13} aria-hidden />
              </a>
            ) : null}
          </div>
        </div>
      </div>

      <Dialog
        open={excludeOpen}
        onClose={() => setExcludeOpen(false)}
        title="Exclude from recommendations"
        description="The recipe stays in your collection but won't be suggested."
        size="sm"
        footer={
          <>
            <Button variant="ghost" onClick={() => setExcludeOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="danger"
              onClick={() => {
                setExcludeOpen(false)
                void toggleExclusion(true, reason.trim() || undefined)
              }}
            >
              Exclude
            </Button>
          </>
        }
      >
        <Input
          label="Reason"
          value={reason}
          onChange={(event) => setReason(event.target.value)}
          placeholder="Optional"
          data-autofocus
        />
      </Dialog>

      <Dialog
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
        title="Preparation history"
        description="Add a day you forgot to log, or remove one by mistake."
        footer={
          <>
            <Button variant="ghost" onClick={() => setHistoryOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              loading={setPreparations.isPending}
              onClick={() => void saveHistory()}
            >
              Save
            </Button>
          </>
        }
      >
        {draft === null ? null : <PreparationEditor dates={draft} onChange={setDraft} />}
      </Dialog>

      <ConfirmDialog
        open={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        onConfirm={() => void confirmDelete()}
        loading={remove.isPending}
        destructive
        confirmLabel="Delete recipe"
        title={`Delete ${recipe.name}?`}
        body="This removes the recipe and its ingredient links. This cannot be undone."
      />
    </PageShell>
  )
}
